import { useEffect, useState } from 'react';
import { Outlet, useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { getUserByUrl, checkTCStatus } from '@/lib/api';
import { useActivityTracker } from '@/hooks/useActivityTracker';
import NotificationBell from '@/components/NotificationBell';
import WorkspaceSwitcher from '@/components/WorkspaceSwitcher';
import { 
  Phone, 
  GraduationCap,
  ChevronRight,
  Menu,
  X,
  User,
  Users,
  FolderOpen,
  BookOpen,
  Trophy,
  UserSearch,
  HelpCircle,
  ClipboardCheck,
  ClipboardList,
  Library,
  Target,
  MessageCircle,
  Shield,
  BarChart3,
  Brain
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { FileText } from 'lucide-react';
import GreetingPopup from '@/components/GreetingPopup';
import AppTour, { USER_TOUR_STEPS } from '@/components/AppTour';
import TermsConditionsModal from '@/components/TermsConditionsModal';
import FloatingMascot from '@/components/FloatingMascot';
import { useTheme } from '@/contexts/ThemeContext';
import { ThemeToggle } from '@/components/ThemeToggle';
import EEOCReminderBanner from '@/components/EEOCReminderBanner';

// Logo URL
const INSYRA_LOGO = "/insyra-logo.png";

const navItems = [
  { icon: BarChart3, label: 'My Analytics', path: '' },
  { icon: Phone, label: 'My Calls', path: 'calls' },
  { icon: ClipboardList, label: 'QA Notes', path: 'qa-notes' },
  { icon: UserSearch, label: 'Search Applicants', path: 'search-applicants' },
  { icon: MessageCircle, label: 'AI Communication Coach', path: 'ai-coach', highlight: true },
  { icon: Shield, label: 'EEOC Training', path: 'eeoc' },
  { icon: Brain, label: 'Mock Training', path: 'mock-training' },
  { icon: Library, label: 'Library', path: 'library' },
  { icon: Trophy, label: 'Leaderboard', path: 'leaderboard' },
  { icon: FileText, label: 'My Reports', path: 'reports' },
  { icon: Target, label: 'KPI', path: 'kpi' },
  { icon: FolderOpen, label: 'Repository', path: 'repository' },
  { icon: BookOpen, label: 'Scoring Guide', path: 'scoring-guide' },
  { icon: Users, label: 'Know the Team', path: 'team' },
  { icon: MessageCircle, label: 'Support', path: 'support' },
  { icon: HelpCircle, label: 'Documentation', path: 'documentation' },
];

export default function UserLayout() {
  const { userUrl } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showGreeting, setShowGreeting] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [showTCModal, setShowTCModal] = useState(false);
  const [tcAccepted, setTcAccepted] = useState(false);
  const [checkingTC, setCheckingTC] = useState(true);
  
  // Activity tracking for online time
  useActivityTracker(user?.id, !!user);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await getUserByUrl(userUrl);
        // Check if user is active
        if (data.is_active === false) {
          setError('This access link has been deactivated. Please contact your administrator.');
          return;
        }
        setUser(data);
        setError(null);
        
        // Check T&C status
        try {
          const tcResponse = await checkTCStatus(data.id, data.tenant_id);
          if (tcResponse.data.has_accepted) {
            setTcAccepted(true);
            setCheckingTC(false);
            
            // Show greeting and tour only after T&C is accepted
            const sessionKey = `greeting_shown_${userUrl}`;
            if (!sessionStorage.getItem(sessionKey)) {
              setShowGreeting(true);
              sessionStorage.setItem(sessionKey, 'true');
            }
            
            const tourCompleted = localStorage.getItem('user_tour_completed');
            if (!tourCompleted) {
              setTimeout(() => {
                setShowTour(true);
              }, showGreeting ? 4500 : 500);
            }
          } else {
            // Show T&C modal
            setShowTCModal(true);
            setCheckingTC(false);
          }
        } catch (tcError) {
          console.error('Error checking T&C status:', tcError);
          // If T&C check fails, show modal to be safe
          setShowTCModal(true);
          setCheckingTC(false);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        setError('User not found. This access link may be invalid or expired.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchUser();
  }, [userUrl, navigate]);

  // Handle T&C acceptance
  const handleTCAccepted = () => {
    setShowTCModal(false);
    setTcAccepted(true);
    
    // Now show greeting and tour
    const sessionKey = `greeting_shown_${userUrl}`;
    if (!sessionStorage.getItem(sessionKey)) {
      setShowGreeting(true);
      sessionStorage.setItem(sessionKey, 'true');
    }
    
    const tourCompleted = localStorage.getItem('user_tour_completed');
    if (!tourCompleted) {
      setTimeout(() => {
        setShowTour(true);
      }, showGreeting ? 4500 : 500);
    }
  };

  const handleStartTour = () => {
    setShowTour(true);
  };

  if (isLoading) {
    return (
      <div className="flex h-screen bg-muted/50 dark:bg-slate-900">
        <div className="w-64 bg-slate-900 p-4">
          <Skeleton className="h-8 w-32 mb-8 bg-slate-800" />
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-10 w-full mb-2 bg-slate-800" />
          ))}
        </div>
        <div className="flex-1 p-8">
          <Skeleton className="h-10 w-64 mb-8" />
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="flex h-screen bg-muted/50 dark:bg-slate-900 items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="mb-6">
            <img src={INSYRA_LOGO} alt="Insyra AI" className="h-16 w-auto mx-auto mb-4" />
          </div>
          <h1 className="text-2xl font-bold text-foreground dark:text-slate-200 mb-2">
            Access Link Not Found
          </h1>
          <p className="text-muted-foreground dark:text-muted-foreground mb-6">
            {error || 'This access link may be invalid or expired. Please contact your administrator for a new link.'}
          </p>
          <div className="space-y-3">
            <Button 
              onClick={() => window.location.href = '/login'}
              className="w-full"
            >
              Go to Login
            </Button>
            <p className="text-sm text-muted-foreground dark:text-muted-foreground">
              If you believe this is an error, please contact your team administrator.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const currentPath = location.pathname.split('/').pop();

  return (
    <div className="flex h-screen bg-muted/50 dark:bg-slate-900">
      {/* Terms & Conditions Modal - Blocking */}
      {showTCModal && user && (
        <TermsConditionsModal
          user={user}
          tenantName={user.tenant_name || user.company || 'Your Organization'}
          tenantId={user.tenant_id || user.tenantId || user.company_id || 'default'}
          onAccepted={handleTCAccepted}
        />
      )}

      {/* Greeting Popup */}
      {showGreeting && tcAccepted && (
        <GreetingPopup 
          userName={user.name?.split(' ')[0]} 
          onClose={() => setShowGreeting(false)} 
        />
      )}

      {/* App Tour */}
      {showTour && tcAccepted && (
        <AppTour 
          steps={USER_TOUR_STEPS}
          onComplete={() => setShowTour(false)}
          isAdmin={false}
        />
      )}

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-slate-900 transform transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="absolute inset-0 sidebar-texture pointer-events-none" />
        
        <div className="relative h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-slate-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={INSYRA_LOGO} alt="Insyra AI" className="h-10 w-auto" />
                <div>
                  <p className="text-xs text-muted-foreground mt-1">Voice → Insight → Impact</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-muted-foreground"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Nav */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const isActive = currentPath === item.path || 
                (item.path === '' && currentPath === userUrl);
              
              return (
                <Link
                  key={item.path}
                  to={`/user/${userUrl}/${item.path}`}
                  className={`sidebar-item ${isActive ? 'sidebar-item-active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                  {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
                </Link>
              );
            })}
            
            {/* Tour Button */}
            <button
              onClick={handleStartTour}
              className="sidebar-item w-full text-left mt-4 border-t border-slate-800 pt-4"
            >
              <HelpCircle className="h-5 w-5" />
              <span className="font-medium">Take a Tour</span>
            </button>
          </nav>

          {/* User info */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 px-4 py-3 bg-slate-800/50 rounded-sm">
              <Avatar className="h-10 w-10">
                <AvatarImage src={user.avatar_url} alt={user.name} />
                <AvatarFallback className="bg-slate-700 text-slate-200">
                  {user.name?.charAt(0) || <User className="h-4 w-4" />}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user.designation || user.role?.name}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="glass-header px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
          
          <div className="flex-1 lg:ml-0 ml-4">
            <h2 className="text-lg font-semibold text-foreground font-heading">
              {navItems.find(n => n.path === currentPath || (n.path === '' && currentPath === userUrl))?.label || 'Dashboard'}
            </h2>
          </div>

          {/* Tour button in header */}
          <div className="flex items-center gap-2">
            <WorkspaceSwitcher />
            <NotificationBell 
              userId={user?.id || user?.user_id}
              userRole="user"
              tenantId={user?.tenant_id}
            />
            <ThemeToggle variant="icon" />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleStartTour}
              className="text-muted-foreground hover:text-foreground dark:text-muted-foreground dark:hover:text-slate-200"
            >
              <HelpCircle className="h-4 w-4 mr-1" />
              Tour
            </Button>
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 overflow-auto p-6 lg:p-8">
          {/* EEOC Training Reminder Banner */}
          <EEOCReminderBanner 
            userId={user?.id || user?.user_id}
            tenantId={user?.tenant_id}
            userUrl={userUrl}
          />
          
          <Outlet context={{ user, userUrl }} />
        </div>
      </main>

      {/* Floating Support Mascot */}
      <FloatingMascot 
        userId={user.id}
        userName={user.name}
        userEmail={user.email}
      />
    </div>
  );
}
